import { Label } from '@kmgestao/ui';

import type { FormField } from '../../schemas.js';

export interface RadioFieldProps {
  field: FormField;
  value: string;
  onChange: (next: string) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Grupo de botões de rádio (escolha única). Diferente de `select`,
 * mostra **todas as opções visíveis** — melhor para listas curtas
 * (≤ 5 opções) onde ver todas as alternativas ajuda a decidir.
 *
 * **Mobile-first**: cada opção é um label inteiro clicável
 * (`flex` com `<input>` embutido) — tap target bem maior que só o
 * círculo do radio, atinge confortavelmente os 44×44 px exigidos.
 *
 * **Acessibilidade**: `role="radiogroup"` com `aria-labelledby`
 * apontando para o Label. Inputs têm `id` único e label associado.
 */
export function RadioField({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
}: RadioFieldProps) {
  const groupId = `${idPrefix}-${field.id}`;
  const labelId = `${groupId}-lbl`;
  const descId = field.description ? `${groupId}-desc` : undefined;
  const errorId = error ? `${groupId}-err` : undefined;
  const options = field.options ?? [];

  return (
    <div className="space-y-2">
      <Label id={labelId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
        className="space-y-2"
      >
        {options.map((opt, idx) => {
          const optId = `${groupId}-opt-${idx}`;
          const checked = value === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={optId}
              className={[
                'flex min-h-11 cursor-pointer items-center gap-3 rounded-md border bg-background px-3 py-2',
                'text-base transition-colors hover:bg-muted/50',
                'disabled:cursor-not-allowed disabled:opacity-50',
                checked ? 'border-primary ring-1 ring-primary/20' : 'border-input',
              ].join(' ')}
            >
              <input
                id={optId}
                type="radio"
                name={groupId}
                value={opt.value}
                checked={checked}
                onChange={() => onChange(opt.value)}
                onBlur={onBlur}
                disabled={disabled}
                className="size-4 accent-primary"
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
